import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { routes } from '@/lib/routes';
import type { ReportDraft } from '@/types/coach';
import { Button } from '@/components/ui/button';

interface Props {
  clientId: string;
  onClose: () => void;
}

type Phase =
  | { state: 'loading' }
  | { state: 'error'; message: string; status: number | null }
  | { state: 'edit' }
  | { state: 'saving' };

function parseError(err: unknown): { message: string; status: number | null } {
  if (err instanceof Error) {
    const match = err.message.match(/^API (\d+): (.*)$/s);
    if (match) {
      const status = Number(match[1]);
      const body = match[2];
      try {
        const parsed = JSON.parse(body);
        if (parsed?.error) return { message: String(parsed.error), status };
      } catch {
        // fall through
      }
      return { message: body, status };
    }
    return { message: err.message, status: null };
  }
  return { message: 'Unbekannter Fehler.', status: null };
}

export function ReportGenerateModal({ clientId, onClose }: Props) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ state: 'loading' });
  const [title, setTitle] = useState('');
  const [fields, setFields] = useState<ReportDraft>({
    ausgangslage: '',
    verlauf: '',
    stand: '',
    ausblick: '',
  });

  function fetchDraft() {
    setPhase({ state: 'loading' });
    api.coach.reports
      .draft(clientId)
      .then((res) => {
        setFields(res.draft);
        setPhase({ state: 'edit' });
      })
      .catch((err) => {
        const parsed = parseError(err);
        setPhase({ state: 'error', message: parsed.message, status: parsed.status });
      });
  }

  useEffect(() => {
    let cancelled = false;
    setPhase({ state: 'loading' });
    api.coach.reports
      .draft(clientId)
      .then((res) => {
        if (cancelled) return;
        setFields(res.draft);
        setPhase({ state: 'edit' });
      })
      .catch((err) => {
        if (cancelled) return;
        const parsed = parseError(err);
        setPhase({ state: 'error', message: parsed.message, status: parsed.status });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && phase.state !== 'saving') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, phase.state]);

  async function handleSave() {
    setPhase({ state: 'saving' });
    try {
      const res = await api.coach.reports.create(clientId, {
        title: title.trim() || null,
        ...fields,
      });
      navigate(routes.coach.report(clientId, res.id));
      onClose();
    } catch (err) {
      const parsed = parseError(err);
      setPhase({ state: 'error', message: parsed.message, status: parsed.status });
    }
  }

  function setField(key: keyof ReportDraft, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  const FIELD_LABELS: Record<keyof ReportDraft, string> = {
    ausgangslage: 'Ausgangslage',
    verlauf: 'Verlauf',
    stand: 'Aktueller Stand',
    ausblick: 'Ausblick',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Neuen Bericht erstellen</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={phase.state === 'saving'}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {phase.state === 'loading' && (
          <div className="my-12 text-center">
            <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">
              KI erstellt Berichts-Entwurf…
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Das dauert typischerweise 5–15 Sekunden.
            </p>
          </div>
        )}

        {phase.state === 'error' && (
          <div className="my-8 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <p className="text-destructive">{phase.message}</p>
            <div className="mt-3 flex gap-2">
              {phase.status !== 422 && (
                <Button size="sm" variant="outline" onClick={fetchDraft}>
                  Erneut versuchen
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onClose}>
                Schließen
              </Button>
            </div>
          </div>
        )}

        {(phase.state === 'edit' || phase.state === 'saving') && (
          <>
            <p className="mt-2 text-xs text-muted-foreground">
              Pass die KI-Vorschläge an, bevor du den Bericht speicherst.
            </p>

            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">
                  Titel (optional)
                </span>
                <input
                  type="text"
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="z. B. Halbjahres-Reflexion"
                  disabled={phase.state === 'saving'}
                />
              </label>

              {(Object.keys(FIELD_LABELS) as Array<keyof ReportDraft>).map((key) => (
                <label key={key} className="block text-sm">
                  <span className="mb-1 block font-medium">{FIELD_LABELS[key]}</span>
                  <textarea
                    rows={5}
                    className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                    value={fields[key]}
                    onChange={(e) => setField(key, e.target.value)}
                    disabled={phase.state === 'saving'}
                  />
                </label>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={phase.state === 'saving'}
              >
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={phase.state === 'saving'}
              >
                {phase.state === 'saving' ? 'Speichern…' : 'Bericht speichern'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
